import { CoreLanguageType, LanguageType, LocaleDetails, proxyLanguageRecord } from 'shared/src/types/languages.type';
import { replacePlaceholders } from './price-ui-content';

/**
 * Copy for the buy slot when a product is not simply sold at retail
 * (purchase-channel.ts), and for prices we did not observe (2026-09-28 QA:
 * `trust-buy-buttons-open-wrong-destination`, `trust-price-range-shown-as-observed-price`,
 * `trust-live-deal-price-is-stale`).
 *
 * Every string is visible and set in all 24 core languages (the type requires
 * it). Plain words, no em dashes.
 *
 * `{country}` is the market's name from Intl.DisplayNames; languages whose
 * grammar puts an article or a case on a country name ("au Canada", "aux
 * États-Unis") set it apart in parentheses instead.
 */
type PurchaseChannelRow = {
  /** The notice on a prescription hearing aid, in place of a Buy button. */
  clinicHearingLabel: string;
  clinicHearingGuidance: string;
  /** The notice on a product not sold in the page's market. */
  notSoldLabel: string;
  notSoldGuidance: string;
  /** The brand's own product page: information, never a place to buy. */
  officialSite: string;
  /** Before a price range a model estimated: it was never observed at a store.
   *  One word, no "price": the figure beside it says it is one. */
  estimatedPrice: string;
  /** Under a price whose deal we cannot vouch for today: `{date}` it was read. */
  priceSeen: string;
  /** The single-destination CTA to a site (a software vendor, a store in the
   *  app shell). Was set in four languages only: the rest showed a blank pill. */
  visitWebsite: string;
};

const purchaseChannelUiContentCore: Record<CoreLanguageType, PurchaseChannelRow> = {
  en: {
    clinicHearingLabel: 'Sold and fitted by hearing care clinics',
    clinicHearingGuidance: 'A hearing care professional fits it after a hearing test.',
    notSoldLabel: 'Not sold in {country}',
    notSoldGuidance: 'No store we track sells it in this market.',
    officialSite: 'Official site',
    estimatedPrice: 'Estimated',
    priceSeen: 'Price seen {date}',
    visitWebsite: 'Visit Website',
  },
  fr: {
    clinicHearingLabel: 'Vendu et ajusté par les cliniques auditives',
    clinicHearingGuidance: "Un audioprothésiste l'ajuste après un test auditif.",
    notSoldLabel: 'Non vendu ici ({country})',
    notSoldGuidance: 'Aucun magasin que nous suivons ne le vend dans ce marché.',
    officialSite: 'Site officiel',
    estimatedPrice: 'Estimé',
    priceSeen: 'Prix relevé le {date}',
    visitWebsite: 'Visiter le site',
  },
  es: {
    clinicHearingLabel: 'Lo venden y adaptan las clínicas auditivas',
    clinicHearingGuidance: 'Un audioprotesista lo adapta tras una prueba de audición.',
    notSoldLabel: 'No se vende en {country}',
    notSoldGuidance: 'Ninguna tienda que seguimos lo vende en este mercado.',
    officialSite: 'Sitio oficial',
    estimatedPrice: 'Estimado',
    priceSeen: 'Precio visto el {date}',
    visitWebsite: 'Visitar sitio',
  },
  de: {
    clinicHearingLabel: 'Verkauf und Anpassung durch Hörakustiker',
    clinicHearingGuidance: 'Ein Hörakustiker passt es nach einem Hörtest an.',
    notSoldLabel: 'Nicht erhältlich ({country})',
    notSoldGuidance: 'Kein von uns verfolgter Händler verkauft es in diesem Markt.',
    officialSite: 'Offizielle Website',
    estimatedPrice: 'Geschätzt',
    priceSeen: 'Preis gesehen am {date}',
    visitWebsite: 'Website besuchen',
  },
  it: {
    clinicHearingLabel: 'Venduto e regolato dai centri acustici',
    clinicHearingGuidance: "Un audioprotesista lo regola dopo un esame dell'udito.",
    notSoldLabel: 'Non in vendita qui ({country})',
    notSoldGuidance: 'Nessun negozio che seguiamo lo vende in questo mercato.',
    officialSite: 'Sito ufficiale',
    estimatedPrice: 'Stimato',
    priceSeen: 'Prezzo rilevato il {date}',
    visitWebsite: 'Visita il sito',
  },
  'pt-BR': {
    clinicHearingLabel: 'Vendido e adaptado por clínicas auditivas',
    clinicHearingGuidance: 'Um fonoaudiólogo o adapta após um exame de audição.',
    notSoldLabel: 'Não vendido aqui ({country})',
    notSoldGuidance: 'Nenhuma loja que acompanhamos o vende neste mercado.',
    officialSite: 'Site oficial',
    estimatedPrice: 'Estimado',
    priceSeen: 'Preço visto em {date}',
    visitWebsite: 'Visitar site',
  },
  'pt-PT': {
    clinicHearingLabel: 'Vendido e adaptado por clínicas auditivas',
    clinicHearingGuidance: 'Um audioprotesista adapta-o após um teste auditivo.',
    notSoldLabel: 'Não vendido aqui ({country})',
    notSoldGuidance: 'Nenhuma loja que acompanhamos o vende neste mercado.',
    officialSite: 'Site oficial',
    estimatedPrice: 'Estimado',
    priceSeen: 'Preço visto a {date}',
    visitWebsite: 'Visitar site',
  },
  nl: {
    clinicHearingLabel: 'Verkocht en aangepast door audiciens',
    clinicHearingGuidance: 'Een audicien past het aan na een gehoortest.',
    notSoldLabel: 'Hier niet te koop ({country})',
    notSoldGuidance: 'Geen winkel die we volgen verkoopt het in deze markt.',
    officialSite: 'Officiële website',
    estimatedPrice: 'Geschat',
    priceSeen: 'Prijs gezien op {date}',
    visitWebsite: 'Website bezoeken',
  },
  pl: {
    clinicHearingLabel: 'Sprzedawany i dopasowywany przez gabinety protetyki słuchu',
    clinicHearingGuidance: 'Protetyk słuchu dopasowuje go po badaniu słuchu.',
    notSoldLabel: 'Niedostępny w sprzedaży ({country})',
    notSoldGuidance: 'Żaden śledzony przez nas sklep nie sprzedaje go na tym rynku.',
    officialSite: 'Oficjalna strona',
    estimatedPrice: 'Szacunkowo',
    priceSeen: 'Cena z {date}',
    visitWebsite: 'Odwiedź stronę',
  },
  sv: {
    clinicHearingLabel: 'Säljs och provas ut av hörselkliniker',
    clinicHearingGuidance: 'En audionom provar ut den efter ett hörseltest.',
    notSoldLabel: 'Säljs inte i {country}',
    notSoldGuidance: 'Ingen butik vi följer säljer den på den här marknaden.',
    officialSite: 'Officiell webbplats',
    estimatedPrice: 'Uppskattat',
    priceSeen: 'Pris sett {date}',
    visitWebsite: 'Besök webbplatsen',
  },
  cy: {
    clinicHearingLabel: "Yn cael ei werthu a'i osod gan glinigau clyw",
    clinicHearingGuidance: "Mae arbenigwr gofal clyw yn ei osod ar ôl prawf clyw.",
    notSoldLabel: 'Ddim ar werth yma ({country})',
    notSoldGuidance: "Does dim siop rydyn ni'n ei dilyn yn ei werthu yn y farchnad hon.",
    officialSite: 'Gwefan swyddogol',
    estimatedPrice: 'Amcangyfrifedig',
    priceSeen: 'Pris a welwyd {date}',
    visitWebsite: 'Ewch i\'r wefan',
  },
  ga: {
    clinicHearingLabel: 'Díolta agus feistithe ag clinicí éisteachta',
    clinicHearingGuidance: 'Feistíonn gairmí cúraim éisteachta é tar éis tástáil éisteachta.',
    notSoldLabel: 'Níl sé ar díol anseo ({country})',
    notSoldGuidance: 'Níl aon siopa a leanaimid á dhíol sa mhargadh seo.',
    officialSite: 'Suíomh oifigiúil',
    estimatedPrice: 'Measta',
    priceSeen: 'Praghas a chonacthas {date}',
    visitWebsite: 'Tabhair cuairt ar an suíomh',
  },
  gd: {
    clinicHearingLabel: "Air a reic 's air a chur air dòigh le clionaigean claisneachd",
    clinicHearingGuidance: 'Cuiridh proifeiseantach cùram claisneachd air dòigh e an dèidh deuchainn claisneachd.',
    notSoldLabel: 'Chan eil e ga reic an seo ({country})',
    notSoldGuidance: 'Chan eil bùth sam bith a leanas sinn ga reic sa mhargadh seo.',
    officialSite: 'Làrach oifigeil',
    estimatedPrice: 'Tuairmeasach',
    priceSeen: 'Prìs a chunnacas {date}',
    visitWebsite: 'Tadhail air an làrach',
  },
  hi: {
    clinicHearingLabel: 'श्रवण देखभाल क्लीनिक द्वारा बेचा और फिट किया जाता है',
    clinicHearingGuidance: 'श्रवण जांच के बाद श्रवण विशेषज्ञ इसे फिट करते हैं।',
    notSoldLabel: 'यहाँ नहीं बिकता ({country})',
    notSoldGuidance: 'हम जिन दुकानों पर नज़र रखते हैं, उनमें से कोई इसे इस बाज़ार में नहीं बेचती।',
    officialSite: 'आधिकारिक साइट',
    estimatedPrice: 'अनुमानित',
    priceSeen: 'कीमत {date} को देखी गई',
    visitWebsite: 'वेबसाइट देखें',
  },
  ca: {
    clinicHearingLabel: "El venen i l'adapten els centres auditius",
    clinicHearingGuidance: "Un audioprotesista l'adapta després d'una prova auditiva.",
    notSoldLabel: 'No es ven aquí ({country})',
    notSoldGuidance: 'Cap botiga que seguim no el ven en aquest mercat.',
    officialSite: 'Lloc oficial',
    estimatedPrice: 'Estimat',
    priceSeen: 'Preu vist el {date}',
    visitWebsite: 'Visita el lloc',
  },
  gl: {
    clinicHearingLabel: 'Véndeno e adáptano as clínicas auditivas',
    clinicHearingGuidance: 'Un audioprotesista adáptao despois dunha proba auditiva.',
    notSoldLabel: 'Non se vende aquí ({country})',
    notSoldGuidance: 'Ningunha tenda que seguimos o vende neste mercado.',
    officialSite: 'Sitio oficial',
    estimatedPrice: 'Estimado',
    priceSeen: 'Prezo visto o {date}',
    visitWebsite: 'Visitar o sitio',
  },
  eu: {
    clinicHearingLabel: 'Entzumen-klinikek saldu eta egokitzen dute',
    clinicHearingGuidance: 'Entzumen-profesional batek egokitzen du entzumen-proba baten ondoren.',
    notSoldLabel: 'Ez da hemen saltzen ({country})',
    notSoldGuidance: 'Jarraitzen dugun dendarik ez du merkatu honetan saltzen.',
    officialSite: 'Webgune ofiziala',
    estimatedPrice: 'Zenbatetsia',
    priceSeen: 'Prezioa {date} ikusia',
    visitWebsite: 'Bisitatu webgunea',
  },
  rm: {
    clinicHearingLabel: "Vendì ed adattà da clinicas d'udida",
    clinicHearingGuidance: "In spezialist d'udida l'adatta suenter in test d'udida.",
    notSoldLabel: 'Betg en vendita qua ({country})',
    notSoldGuidance: 'Nagina butia che nus observain na la venda en quest martg.',
    officialSite: 'Pagina uffiziala',
    estimatedPrice: 'Stimà',
    priceSeen: 'Pretsch vis ils {date}',
    visitWebsite: 'Visitar la pagina',
  },
  mi: {
    clinicHearingLabel: 'Ka hokona, ka whakauruhia e ngā whare haumanu rongo',
    clinicHearingGuidance: 'Mā tētahi ngaio tiaki rongo e whakauru i muri i te whakamātautau rongo.',
    notSoldLabel: 'Kāore e hokona ana ki konei ({country})',
    notSoldGuidance: 'Kāore he toa e aru ana mātou e hoko ana i tēnei mākete.',
    officialSite: 'Pae whaimana',
    estimatedPrice: 'Matapae',
    priceSeen: 'I kitea te utu {date}',
    visitWebsite: 'Toro i te pae',
  },
  ta: {
    clinicHearingLabel: 'செவித்திறன் பராமரிப்பு மருத்துவமனைகளால் விற்கப்பட்டு பொருத்தப்படுகிறது',
    clinicHearingGuidance: 'செவித்திறன் பரிசோதனைக்குப் பிறகு ஒரு நிபுணர் இதைப் பொருத்துவார்.',
    notSoldLabel: 'இங்கு விற்கப்படுவதில்லை ({country})',
    notSoldGuidance: 'நாங்கள் கண்காணிக்கும் எந்தக் கடையும் இந்தச் சந்தையில் இதை விற்பதில்லை.',
    officialSite: 'அதிகாரப்பூர்வ தளம்',
    estimatedPrice: 'மதிப்பிடப்பட்டது',
    priceSeen: 'விலை {date} அன்று பார்க்கப்பட்டது',
    visitWebsite: 'இணையதளத்தைப் பார்வையிடவும்',
  },
  te: {
    clinicHearingLabel: 'వినికిడి సంరక్షణ క్లినిక్‌లు విక్రయించి అమర్చుతాయి',
    clinicHearingGuidance: 'వినికిడి పరీక్ష తర్వాత నిపుణులు దీన్ని అమర్చుతారు.',
    notSoldLabel: 'ఇక్కడ విక్రయించబడదు ({country})',
    notSoldGuidance: 'మేము గమనించే ఏ దుకాణమూ ఈ మార్కెట్‌లో దీన్ని విక్రయించదు.',
    officialSite: 'అధికారిక సైట్',
    estimatedPrice: 'అంచనా',
    priceSeen: 'ధర {date}న చూడబడింది',
    visitWebsite: 'వెబ్‌సైట్‌ను సందర్శించండి',
  },
  bn: {
    clinicHearingLabel: 'শ্রবণ পরিচর্যা ক্লিনিক বিক্রি ও ফিট করে',
    clinicHearingGuidance: 'শ্রবণ পরীক্ষার পর একজন বিশেষজ্ঞ এটি ফিট করেন।',
    notSoldLabel: 'এখানে বিক্রি হয় না ({country})',
    notSoldGuidance: 'আমরা যে দোকানগুলো দেখি, তার কোনোটিই এই বাজারে এটি বিক্রি করে না।',
    officialSite: 'অফিসিয়াল সাইট',
    estimatedPrice: 'আনুমানিক',
    priceSeen: 'দাম দেখা হয়েছে {date}',
    visitWebsite: 'ওয়েবসাইট দেখুন',
  },
  mr: {
    clinicHearingLabel: 'श्रवण काळजी क्लिनिकद्वारे विकले व बसवले जाते',
    clinicHearingGuidance: 'श्रवण चाचणीनंतर तज्ज्ञ ते बसवतात.',
    notSoldLabel: 'येथे विकले जात नाही ({country})',
    notSoldGuidance: 'आम्ही पाहत असलेले कोणतेही दुकान या बाजारात ते विकत नाही.',
    officialSite: 'अधिकृत साइट',
    estimatedPrice: 'अंदाजे',
    priceSeen: 'किंमत {date} रोजी पाहिली',
    visitWebsite: 'वेबसाइटला भेट द्या',
  },
  ja: {
    clinicHearingLabel: '補聴器専門店で販売・調整されます',
    clinicHearingGuidance: '聴力検査の後、専門家が調整します。',
    notSoldLabel: '{country}では販売されていません',
    notSoldGuidance: '当サイトが確認している店舗では、この市場で販売されていません。',
    officialSite: '公式サイト',
    estimatedPrice: '推定',
    priceSeen: '{date}時点の価格',
    visitWebsite: 'サイトを見る',
  },
};

const purchaseChannelUiContent: Record<LanguageType, PurchaseChannelRow> =
  proxyLanguageRecord<PurchaseChannelRow>(purchaseChannelUiContentCore);

/** The copy for a page language. */
export function getPurchaseChannelUiContent(languageCode: LanguageType): PurchaseChannelRow {
  return purchaseChannelUiContent[languageCode];
}

/** The market's name in the page's language ("Canada", "États-Unis"). */
export function marketName(localeDetails: Pick<LocaleDetails, 'path' | 'regionCode'>): string {
  const iso = localeDetails.path.split('-')[1] ?? localeDetails.regionCode;
  const code = iso === 'UK' ? 'GB' : iso;
  try {
    return new Intl.DisplayNames([localeDetails.path], { type: 'region' }).of(code) ?? iso;
  } catch {
    return iso;
  }
}

/**
 * The one line that stands where a Buy button would, for a product not sold
 * at retail here: "Sold and fitted by hearing care clinics", "Not sold in
 * Canada". The product card's notice leads with it, and the search shelf's
 * card shows it alone.
 */
export function purchaseChannelLabel(
  channel: 'clinic' | 'not-in-market',
  localeDetails: Pick<LocaleDetails, 'path' | 'regionCode' | 'languageCode'>,
): string {
  const copy = getPurchaseChannelUiContent(localeDetails.languageCode);
  return channel === 'clinic'
    ? copy.clinicHearingLabel
    : replacePlaceholders(copy.notSoldLabel, { country: marketName(localeDetails) });
}
